import { request } from './client'

/* ============================================================================
   W-124 — THE ACTIVITY LOG.

   **READ-ONLY, AND THERE IS DELIBERATELY NOTHING IN THIS FILE THAT WRITES.** Pack 5:
   "Entries are never edited or removed; retention is a deletion at 24 months, not a
   rewrite." A client that could edit one would make every other row in the table
   arguable, so the absence here is part of the design rather than work not yet done.

   THE WORDS ARE NOT IN THIS FILE EITHER. A verb, a category, a before and an after all
   arrive as TOKENS and the screen turns them into sentences in the reader's own language.
   That is the same rule as every other enum on this wire, and it exists because the one
   screen an Arabic-speaking administrator opens after an incident must not be the one
   screen in English.
   ========================================================================= */

/** The three tabs pack 5 draws, plus the absence of one. */
export type ActivityFilter = 'PERMISSION_CHANGE' | 'OVERRIDE' | 'SIGN_IN_REFUSED'

export interface ActivityRow {
  /** Also the paging cursor, and the list's stable key. */
  entryId: number
  atUtc: string
  /** A token — `ROOM_OVERRIDDEN`. The screen has the sentence. */
  verb: string
  category: string
  /**
   * Who, AS THE LOG FROZE IT. Not a join: this codebase's own sign-in endpoint warns that
   * reissuing an employee number makes one person inherit another's audit trail, and a log
   * that resolved names at read time would rewrite itself retroactively when that happened.
   */
  actor: string
  actorAr: string | null
  /** ROOM, USER, ROLE, SESSION, SIGN_IN, ISSUE, STOCK_ITEM, TEMPLATE, TASK. */
  subjectType: string
  subject: string
  subjectAr: string | null
  /**
   * A token where the value is one (`ALLOWED_WITH_REASON`, `DIRTY`) and a plain value
   * otherwise (`404`, `v7`, `-4`). The screen looks each up and falls back to drawing it
   * as it stands — so a token nobody has written a sentence for is still legible, which on
   * this screen matters more than anywhere else: a missing translation must never hide
   * what happened.
   */
  before: string | null
  after: string | null
  reason: string | null
  source: string
  sourceDetail: string | null
}

export interface ActivityPage {
  rows: ActivityRow[]
  /** Null on the last page. **Not the same as an empty page** — the screen says "nothing
      here" for one and simply stops offering "show older" for the other. */
  nextCursor: number | null
  /** Sent rather than hardcoded: it is the answer to "why is there nothing from last
      March", and the person asking deserves to be told by the screen. */
  retentionMonths: number
}

export interface ActivityQuery {
  filter?: ActivityFilter
  /** A DATE, `YYYY-MM-DD`. The server makes `to` inclusive of its whole day — a
      supervisor filtering "up to the 14th" and shown nothing from the 14th would conclude
      nothing happened that day. */
  from?: string
  to?: string
  cursor?: number
  limit?: number
}

const query = (q: ActivityQuery): string => {
  const parts: string[] = []
  if (q.filter) parts.push(`filter=${encodeURIComponent(q.filter)}`)
  if (q.from) parts.push(`from=${encodeURIComponent(q.from)}`)
  if (q.to) parts.push(`to=${encodeURIComponent(q.to)}`)
  if (q.cursor !== undefined) parts.push(`cursor=${q.cursor}`)
  if (q.limit !== undefined) parts.push(`limit=${q.limit}`)
  return parts.length === 0 ? '' : `?${parts.join('&')}`
}

export const readActivity = (q: ActivityQuery = {}, signal?: AbortSignal) =>
  request<ActivityPage>(`/activity${query(q)}`, signal ? { signal } : {})

/**
 * THE SAME ROWS, AS A FILE.
 *
 * **A BLOB THROUGH THE AUTHENTICATED CLIENT, NOT A LINK.** A plain `<a href>` cannot carry
 * the `Authorization` header, so a downloadable link would have to be a second,
 * unauthenticated door into two years of who did what — which is the shape of #81 and #89,
 * where a second way in went on answering after the first had stopped. `expect: 'blob'` is
 * the path W-136's printed card sheet and W-110's PDF already use, for the same reason.
 *
 * CSV rather than the reporting framework's PDF, because the two are for different things:
 * a report is a document somebody reads, and an audit export is evidence somebody filters,
 * sorts and hands to a person who was not there.
 */
export const exportActivityCsv = (q: ActivityQuery = {}) =>
  request<Blob>(
    `/activity/export${query({ ...q, cursor: undefined, limit: undefined })}`,
    { expect: 'blob' },
  )

/** What the file should be called once the browser has it. The filter and the date are in
    the name because the second question about any export that reaches an inbox is "what is
    this and when is it from", and `export.csv` cannot answer it. */
export const activityExportName = (q: ActivityQuery = {}) =>
  `activity-${(q.filter ?? 'all').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`

/** The actor and subject, in the reader's language, falling back to the English — the
    same rule as every other name in this product. */
export const activityActor = (row: ActivityRow, locale: 'en' | 'ar') =>
  locale === 'ar' ? row.actorAr ?? row.actor : row.actor

export const activitySubject = (row: ActivityRow, locale: 'en' | 'ar') =>
  locale === 'ar' ? row.subjectAr ?? row.subject : row.subject
